/* eslint-disable max-lines */
import {
  AlarmStatus,
  LocationAlarmStatus,
  LocationAlarmUpdate,
  LocationAlarmUpdatesDTO,
  LocationAlarmUpdateType,
  ModifyAlarmUpdateEntry,
  ScanAlarmUpdateType,
  TwilioLogType,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import {
  arrivalTimelineIcon,
  autoresolvedTimelineIcon,
  callMadeIcon,
  checkTimelineIcon,
  commentTimelineIcon,
  dispatchTimelineIcon,
  escalationOpenTimelineIcon,
  hakimoTimelineIcon,
  inProgressTimelineIcon,
  orphanTimelineIcon,
  pendingTimelineIcon,
  resolvedTimelineIcon,
  smsTimelineIcon,
  speakerTalkdownTimelineIcon,
  TimelineEvent,
  viewTimelineIcon,
} from '@hakimo-ui/hakimo/ui-elements';
import { Selectable } from '@hakimo-ui/shared/ui-base';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { getCallTimeRangeNode } from '../../twilio-call-details/extraDetailsUtil';
import { getTwilioLogDescriptionNode } from '../../twilio-sms-details/getTwilioLogDescriptionNode';
import { isSmsLog } from '../../twilio-sms-details/utils';
import { mapStatusToStatusType } from '../utils';
import {
  getManualTalkdownDetailsNode,
  getManualTalkdownModalContentNode,
  getNewAlarmEventDetailsModalContentNode,
  getNewAlarmEventDetailsNode,
  getSituationDetailsModalContentNode,
  getSituationDetailsNode,
  getSnapshotImagesNode,
} from './extraDetailsUtil';
dayjs.extend(utc);
dayjs.extend(timezone);
type TimelineEventSortable = TimelineEvent & { timestamp: number };

export interface UpdateObject {
  user?: Selectable;
  status?: AlarmStatus | LocationAlarmStatus;
  event?: string;
  comment?: string;
  twilio_log_type?: TwilioLogType;
  twilio_ssid?: string;
  twilio_calling_to_name?: string;
  escalation_comment?: string;
  operator_id?: string;
  update_text?: string;
  recommendation?: string;
  summary?: string;
  resolution_comment?: string;
  resolution_reason?: string;
  incident_category?: string;
  responder_type?: string;
  dispatch_status?: string;
  situation?: string;
  s3_urls?: string[];
}

export function getResponderLabel(responderType?: string): string {
  const norm = (responderType ?? '').toUpperCase();
  if (norm === 'POLICE') return 'Police';
  if (norm === 'SECURITY') return 'Security';
  return '';
}

export function formatDispatchUpdateLabel(
  updateType: string,
  responderType?: string,
  dispatchStatus?: string
): string {
  const responder = getResponderLabel(responderType);
  const prefix = responder ? `${responder} ` : '';

  const isArrival =
    updateType === LocationAlarmUpdateType.ARRIVAL ||
    updateType === ScanAlarmUpdateType.ARRIVAL;
  if (isArrival) return `${prefix}Arrived`;

  const statusNorm = (dispatchStatus ?? '').toUpperCase();
  if (statusNorm === 'CANCELLED') return `${prefix}Dispatch Cancelled`;
  if (statusNorm === 'DENIED') return `${prefix}Dispatch Denied`;
  if (statusNorm === 'ACCEPTED') return `${prefix}Dispatch Accepted`;
  return `${prefix}Dispatch (unknown)`.trim();
}

function formatLoggedDispatchDescription(update: UpdateObject): string {
  const name = update.user?.name ?? '';
  const prefix = name ? `${name} logged` : 'Logged';
  const label = formatDispatchUpdateLabel(
    update.event ?? '',
    update.responder_type,
    update.dispatch_status
  );
  return `${prefix} ${label}`.trim();
}

const HAKIMO_USERNAME = 'Hakimo AI Engine';
const ADD_COMMENT_EVENT = 'Added a comment';
const ACS_ACKNOWLEDGMENT_EVENT = 'ACS Alarm Acknowledgment';
const SPEAKER_TALKDOWN_EVENT = 'Responded via speaker talk-down';
const TIMELINE_TIME_FORMAT = 'DD/MM/YYYY, HH:mm:ss';

const AI_ENGINE_NAME = 'Hakimo AI Engine';

export function updateTime(
  update: Pick<LocationAlarmUpdate, 'timestamp_utc' | 'update_time'>
): string | number {
  const ts = update.timestamp_utc;
  return ts && dayjs(ts).isValid() ? ts : update.update_time;
}

export function displayTime(
  update: Pick<LocationAlarmUpdate, 'event_time_utc' | 'update_time'>,
  pending?: Partial<ModifyAlarmUpdateEntry>
): string {
  return (
    pending?.update_timestamp_utc ?? update.event_time_utc ?? update.update_time
  );
}

export function getTimelineIcon(update: UpdateObject): JSX.Element {
  if (update.user?.name === HAKIMO_USERNAME || update.user === null) {
    return hakimoTimelineIcon;
  }
  if (update.status) {
    switch (mapStatusToStatusType(update.status)) {
      case 'Resolved':
        return checkTimelineIcon;
      case 'In Progress':
        return inProgressTimelineIcon;
      case 'Pending':
        return pendingTimelineIcon;
    }
  }
  switch (update.event) {
    case SPEAKER_TALKDOWN_EVENT:
    case LocationAlarmUpdateType.MANUAL_TALKDOWN:
      return speakerTalkdownTimelineIcon;
    case LocationAlarmUpdateType.VIEW_ALARM:
    case ScanAlarmUpdateType.VIEW_ALARM:
      return viewTimelineIcon;
    case LocationAlarmUpdateType.CALL_SMS_LOGS:
      return isSmsLog(update) ? smsTimelineIcon : callMadeIcon;
    case LocationAlarmUpdateType.CALL_EVENT:
      return callMadeIcon;
    case LocationAlarmUpdateType.ESCALATION_OPEN:
    case ScanAlarmUpdateType.ESCALATION_OPEN:
      return escalationOpenTimelineIcon;
    case LocationAlarmUpdateType.ESCALATION_SNAPSHOT_COMMENT:
    case ScanAlarmUpdateType.COMMENT_FOR_SNAPSHOT:
      return commentTimelineIcon;
    case ScanAlarmUpdateType.ESCALATION_CLOSED:
      return resolvedTimelineIcon;
    case ScanAlarmUpdateType.RESOLVED:
      return resolvedTimelineIcon;
    case ScanAlarmUpdateType.AUTORESOLVED:
      return autoresolvedTimelineIcon;
    case ScanAlarmUpdateType.ADD_EVENT:
    case ScanAlarmUpdateType.LLM_GROUP_EVENT:
    case ScanAlarmUpdateType.LLM_EVENT:
    case ScanAlarmUpdateType.ADD_EVENT_RAW:
      return hakimoTimelineIcon;
    case ScanAlarmUpdateType.RSPNDR:
      return commentTimelineIcon;
    case LocationAlarmUpdateType.ALLOCATION:
    case ScanAlarmUpdateType.ALLOCATION:
      return viewTimelineIcon;
    case LocationAlarmUpdateType.ORPHAN:
    case ScanAlarmUpdateType.ORPHAN:
      return orphanTimelineIcon;
    case LocationAlarmUpdateType.DISPATCH:
    case ScanAlarmUpdateType.DISPATCH:
      return dispatchTimelineIcon;
    case LocationAlarmUpdateType.ARRIVAL:
    case ScanAlarmUpdateType.ARRIVAL:
      return arrivalTimelineIcon;
    default:
      return commentTimelineIcon;
  }
}

export function getEventDescription(
  update: UpdateObject,
  assumeHakimo = false,
  alarmGroupId?: string,
  locationAlarmId?: string
) {
  if (update.status) {
    let description = '';
    if (assumeHakimo) {
      description = `${update.user?.name ?? AI_ENGINE_NAME} changed status to ${
        update.status
      }`;
    } else if (update.user?.name !== undefined) {
      description = `${update.user?.name} changed status to ${update.status}`;
    } else {
      description = `Status changed to ${update.status}`;
    }
    if (update.comment) {
      description = description + `: ${update.comment}`;
    }
    return description;
  }
  if (
    update.event === ADD_COMMENT_EVENT ||
    update.event === LocationAlarmUpdateType.ADD_COMMENT
  ) {
    if (assumeHakimo) {
      return `${update.user?.name ?? AI_ENGINE_NAME}: "${update.comment}"`;
    } else if (update.user?.name !== undefined) {
      return `${update.user?.name}: "${update.comment}"`;
    }
    return `"${update.comment}"`;
  }
  if (update.event === ACS_ACKNOWLEDGMENT_EVENT) {
    return `Alarm Acknowledged via ACS: ${update.comment}`;
  }
  if (
    update.event === SPEAKER_TALKDOWN_EVENT ||
    update.event === LocationAlarmUpdateType.MANUAL_TALKDOWN
  ) {
    if (assumeHakimo) {
      return `${
        update.user?.name ?? AI_ENGINE_NAME
      } responded via speaker talk-down`;
    } else if (update.user?.name !== undefined) {
      return `${update.user?.name} responded via speaker talk-down`;
    }
    return `Speaker talk-down conducted`;
  }
  if (
    update.event === LocationAlarmUpdateType.VIEW_ALARM ||
    update.event === ScanAlarmUpdateType.VIEW_ALARM
  ) {
    if (update.user?.name !== undefined) {
      return `${update.user?.name} viewed the alarm`;
    }
    return 'Alarm was viewed';
  }
  if (update.event === LocationAlarmUpdateType.CALL_SMS_LOGS) {
    return getTwilioLogDescriptionNode(update, {
      alarmGroupId,
      locationAlarmId,
    });
  }
  if (update.event === LocationAlarmUpdateType.ESCALATION_OPEN) {
    const name = update.user?.name ?? '';
    const comment = update.escalation_comment?.trim();
    return `Location alarm escalated${name ? ` by ${name}` : ''}${
      comment ? `\nEscalation Comment: ${comment}` : ''
    }`;
  }
  if (update.event === LocationAlarmUpdateType.CALL_EVENT) {
    return update.comment ?? 'Call event';
  }
  if (
    update.event === LocationAlarmUpdateType.ESCALATION_SNAPSHOT_COMMENT ||
    update.event === ScanAlarmUpdateType.COMMENT_FOR_SNAPSHOT
  ) {
    const name = update.user?.name ?? '';
    return `Evidence added${name ? ` by ${name}` : ''}`;
  }
  switch (update.event) {
    case ScanAlarmUpdateType.ALLOCATION:
    case LocationAlarmUpdateType.ALLOCATION:
      return `${
        update.user?.name
          ? `${
              update.user.name.charAt(0).toUpperCase() +
              update.user.name.slice(1)
            }`
          : ''
      } viewed Alarm Group in Unified monitoring`;
    case ScanAlarmUpdateType.ORPHAN:
    case LocationAlarmUpdateType.ORPHAN:
      return `Alarm Group unassigned`;
    case ScanAlarmUpdateType.ESCALATION_OPEN: {
      const name = update.user?.name ?? '';
      const comment = update.escalation_comment?.trim();
      return `Alarm Group escalated${name ? ` by ${name}` : ''}${
        comment ? `\nEscalation Comment: ${comment}` : ''
      }`;
    }
    case ScanAlarmUpdateType.ESCALATION_CLOSED: {
      const name = update.user?.name ?? '';
      const comment = (
        update.escalation_comment || update.resolution_comment
      )?.trim();
      const resolutionReason = update.resolution_reason?.trim();
      const incidentCategory = update.incident_category?.trim();
      return `Escalation closed${name ? ` by ${name}` : ''}${
        incidentCategory ? `\nIncident Category: ${incidentCategory}` : ''
      }${resolutionReason ? `\nResolution Reason: ${resolutionReason}` : ''}${
        comment ? `\nResolved Comment: ${comment}` : ''
      }`;
    }
    case ScanAlarmUpdateType.RESOLVED: {
      const text = update.user?.name
        ? `${update.user.name} changed status to Resolved`
        : 'Alarm Group resolved';
      const resolutionComment = update.resolution_comment;
      if (resolutionComment) {
        return `${text}: ${resolutionComment}`;
      }
      return text;
    }
    case ScanAlarmUpdateType.STATUS_UPDATE:
      return `${
        update.user?.name ?? AI_ENGINE_NAME
      } ${update?.update_text?.trim()}`;
    case ScanAlarmUpdateType.LLM_GROUP_EVENT:
      return update.user?.name === AI_ENGINE_NAME && update.update_text?.trim()
        ? `${AI_ENGINE_NAME} added feedback: ${update.update_text.trim()}`
        : null;
    case ScanAlarmUpdateType.AUTORESOLVED: {
      const operator = update.operator_id || AI_ENGINE_NAME;
      return `${operator} auto-resolved the alarm${
        update.update_text?.trim() ? `: ${update.update_text.trim()}` : ''
      }`;
    }
    case ScanAlarmUpdateType.LLM_EVENT:
      return `${AI_ENGINE_NAME} added feedback: Recommendation: ${
        update.recommendation?.trim() || ''
      } Summary: ${update.summary?.trim() || ''}`;
    case ScanAlarmUpdateType.ADD_EVENT_RAW: {
      const name = update.user?.name ?? AI_ENGINE_NAME;
      return `${name} added new alarm to alarm group: ${alarmGroupId ?? ''}`;
    }
    case ScanAlarmUpdateType.RSPNDR: {
      const name = update.user?.name ?? '';
      const comment = update.update_text?.trim() ?? '';
      return name
        ? `Rspndr escalation by ${name}${comment ? `: ${comment}` : ''}`
        : comment;
    }
  }
  if (
    update.event === LocationAlarmUpdateType.ARRIVAL ||
    update.event === ScanAlarmUpdateType.ARRIVAL ||
    update.event === LocationAlarmUpdateType.DISPATCH ||
    update.event === ScanAlarmUpdateType.DISPATCH
  ) {
    return formatLoggedDispatchDescription(update);
  }
  if (update.user?.name && update.comment) {
    return `${update.user.name} added feedback: ${update.comment}`;
  }
  return '';
}

export function getLocationAlarmUpdateEvents(
  updates: LocationAlarmUpdatesDTO,
  isSupportRole?: boolean,
  product: UnifiedMonitoringProduct = UnifiedMonitoringProduct.REMOTE_GUARDING,
  showCreatedEvent = true,
  alarmId?: string,
  locationTimezone?: string,
  criticalAlarmIds?: Set<string>
): TimelineEvent[] {
  const isScanAlarmGroup = product === UnifiedMonitoringProduct.SCAN;
  const alarmGroupId = isScanAlarmGroup ? alarmId : undefined;
  const locationAlarmId = isScanAlarmGroup ? undefined : alarmId;
  const events: TimelineEventSortable[] = [];
  const talkdwownsState = updates.talkdowns || [];

  const ADD_EVENT_TYPE = isScanAlarmGroup
    ? ScanAlarmUpdateType.ADD_EVENT
    : LocationAlarmUpdateType.ADD_EVENT;

  const VIEW_ALARM_TYPE = new Set([
    LocationAlarmUpdateType.VIEW_ALARM,
    ScanAlarmUpdateType.VIEW_ALARM,
  ]);

  const sortedAddEvents = updates.items
    .filter((item) => item.update_type === ADD_EVENT_TYPE)
    .sort(
      (a, b) =>
        new Date(updateTime(a)).getTime() - new Date(updateTime(b)).getTime()
    );

  if (sortedAddEvents.length > 0 && showCreatedEvent) {
    const firstDate = dayjs(updateTime(sortedAddEvents[0])).tz(
      locationTimezone
    );
    events.push({
      icon: hakimoTimelineIcon,
      description: isScanAlarmGroup
        ? 'Created alarm group'
        : 'Location alarm created',
      time: firstDate.format(TIMELINE_TIME_FORMAT),
      timestamp: firstDate.valueOf() - 1000,
    });
  }

  const [addEventGroups, remainingUpdates] = updates.items.reduce<
    [LocationAlarmUpdate[][], LocationAlarmUpdate[]]
  >(
    (acc, update) => {
      if (update.update_type === ADD_EVENT_TYPE) {
        acc[0][acc[0].length - 1].push(update);
      } else {
        acc[1].push(update);
        acc[0].push([]);
      }
      return acc;
    },
    [[[]], []]
  );

  addEventGroups.forEach((group) => {
    if (!group.length) return;
    group.sort(
      (a, b) =>
        new Date(updateTime(a)).getTime() - new Date(updateTime(b)).getTime()
    );

    const firstDate = dayjs(updateTime(group[0])).tz(locationTimezone);
    const lastDate = dayjs(updateTime(group[group.length - 1])).tz(
      locationTimezone
    );

    const time =
      group.length === 1
        ? lastDate.format(TIMELINE_TIME_FORMAT)
        : `${firstDate.format(TIMELINE_TIME_FORMAT)} – ${lastDate.format(
            'HH:mm:ss'
          )}`;

    events.push({
      icon: hakimoTimelineIcon,
      description: `Added ${group.length} new alarm${
        group.length > 1 ? 's' : ''
      }`,
      time,
      timestamp: lastDate.valueOf(),
      extraDetails: getNewAlarmEventDetailsNode(
        group,
        talkdwownsState,
        criticalAlarmIds
      ),
      extraDetailsModalContent: getNewAlarmEventDetailsModalContentNode(
        group,
        talkdwownsState,
        isSupportRole,
        criticalAlarmIds
      ),
      extraDetailsModalTitle: 'Talkdown Details',
    });
  });

  const processAndPushEvent = (update: LocationAlarmUpdate) => {
    const updateObj: UpdateObject = {
      user: update.user,
      event: update.update_type,
      comment: update.update_text,
      escalation_comment: update.escalation_comment,
      operator_id: update.operator_id,
      twilio_log_type: update.twilio_log_type,
      twilio_ssid: update.twilio_ssid,
      twilio_calling_to_name: update.twilio_calling_to_name,
      update_text: update.update_text,
      recommendation: update.recommendation,
      summary: update.summary,
      resolution_comment: update.resolution_comment,
      resolution_reason: update.resolution_reason,
      incident_category: update.incident_category,
      responder_type: update.responder_type,
      dispatch_status: update.dispatch_status,
      situation: update.situation,
      s3_urls: update.s3_urls,
    };

    const description = getEventDescription(
      updateObj,
      true,
      alarmGroupId,
      locationAlarmId
    );
    if (!description) return;

    const date = dayjs(updateTime(update)).tz(locationTimezone);
    const time = date.format(TIMELINE_TIME_FORMAT);

    const event: TimelineEventSortable = {
      icon: getTimelineIcon(updateObj),
      description,
      time,
      eventTime: update.event_time_utc
        ? dayjs(update.event_time_utc)
            .tz(locationTimezone)
            .format(TIMELINE_TIME_FORMAT)
        : undefined,
      timestamp: date.valueOf(),
      audio_url: update.audio_url,
    };

    if (
      update.update_type === LocationAlarmUpdateType.CALL_SMS_LOGS &&
      update.twilio_ssid &&
      !isSmsLog(update)
    ) {
      event.timeNode = getCallTimeRangeNode(
        update.twilio_ssid,
        time,
        locationTimezone
      );
    }

    if (update.update_type === LocationAlarmUpdateType.MANUAL_TALKDOWN) {
      event.extraDetails = getManualTalkdownDetailsNode(
        update,
        talkdwownsState
      );
      event.extraDetailsModalContent = getManualTalkdownModalContentNode(
        update,
        talkdwownsState
      );
      event.extraDetailsModalTitle = 'Talkdown Details';
    }

    // Check if this is a snapshot comment update with images
    if (
      update.update_type ===
        LocationAlarmUpdateType.ESCALATION_SNAPSHOT_COMMENT ||
      update.update_type === ScanAlarmUpdateType.COMMENT_FOR_SNAPSHOT
    ) {
      const detailsNode = getSnapshotImagesNode(
        update.escalation_comment,
        update.s3_urls
      );
      if (detailsNode) {
        event.extraDetails = detailsNode;
      }
    }

    if (
      update.update_type === ScanAlarmUpdateType.ARRIVAL ||
      update.update_type === ScanAlarmUpdateType.DISPATCH ||
      update.update_type === LocationAlarmUpdateType.ARRIVAL ||
      update.update_type === LocationAlarmUpdateType.DISPATCH
    ) {
      const detailsNode = getSnapshotImagesNode(
        update.comment ?? update.notes,
        update.s3_urls
      );
      if (detailsNode) {
        event.extraDetails = detailsNode;
      }
    }

    // Check if this is an escalation_open or resolved update with situation editing enabled
    if (
      (update.update_type === ScanAlarmUpdateType.ESCALATION_OPEN ||
        update.update_type === ScanAlarmUpdateType.RESOLVED) &&
      update.is_situation_editing_enabled &&
      isSupportRole
    ) {
      event.extraDetails = getSituationDetailsNode(update);
      event.extraDetailsModalContent = getSituationDetailsModalContentNode(
        update,
        alarmGroupId || ''
      );
      event.extraDetailsModalTitle = 'Situation Details';
    }

    events.push(event);
  };

  const [viewAlarmEvents, allOtherEvents] = remainingUpdates.reduce<
    [LocationAlarmUpdate[][], LocationAlarmUpdate[]]
  >(
    (acc, update) => {
      if (VIEW_ALARM_TYPE.has(update.update_type)) {
        const lastGroup = acc[0][acc[0].length - 1];
        if (!lastGroup.length || update.user?.id === lastGroup[0].user?.id) {
          lastGroup.push(update);
        } else {
          acc[0].push([update]);
        }
      } else {
        acc[1].push(update);
        acc[0].push([]);
      }
      return acc;
    },
    [[[]], []]
  );

  viewAlarmEvents.forEach((group) => {
    if (!group.length) return;

    const lastDate = dayjs(updateTime(group[group.length - 1])).tz(
      locationTimezone
    );

    events.push({
      icon: viewTimelineIcon,
      description: `${group[0].user?.name} viewed this alarm ${
        group.length
      } time${group.length > 1 ? 's' : ''}`,
      time: lastDate.format('DD/MM/YYYY, HH:mm:ss'),
      timestamp: lastDate.valueOf(),
    });
  });

  const allowedScanUpdateTypes = new Set([
    ...Object.values(ScanAlarmUpdateType),
    LocationAlarmUpdateType.CALL_SMS_LOGS,
    LocationAlarmUpdateType.MANUAL_TALKDOWN,
  ]);

  allOtherEvents.forEach((update) => {
    if (!isScanAlarmGroup || allowedScanUpdateTypes.has(update.update_type)) {
      processAndPushEvent(update);
    }
  });

  return events
    .sort((a, b) => b.timestamp - a.timestamp)
    .map((e) => ({
      icon: e.icon,
      description: e.description,
      time: e.time,
      timeNode: e.timeNode,
      eventTime: e.eventTime,
      audio_url: e.audio_url,
      extraDetails: e.extraDetails,
      extraDetailsModalContent: e.extraDetailsModalContent,
      extraDetailsModalTitle: e.extraDetailsModalTitle,
    }));
}
